import { useState, type ReactNode } from 'react';
import { Button, DropdownMenu, Icon, Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../i18n';
import { useWorkspacePath } from '../workspace-path';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE HEADER BREADCRUMB — WHERE AM I, AND WHAT IS THIS CALLED.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for on 14 Sep across four surfaces: a task, a contract, a line item and a project. The
 * header used to be `KEY · title` with a back chevron, which answers *what* and not *where* — and
 * *where* is the question on a screen you arrived at from a list, a board, a search result or a
 * link somebody pasted, four routes that all look identical once you are there.
 *
 * ⚠️⚠️ **THE LAST SEGMENT IS NOT A LINK, AND THAT IS THE WHOLE CONVENTION.** It is the page you are
 * on. Rendering it as a link that navigates to itself is the commonest breadcrumb bug there is:
 * it looks clickable, does nothing visible, and teaches people the trail is decoration.
 *
 * ⚠️ **The key carries a copy button rather than being one.** *"Which ticket?"* is answered by
 * pasting `HSO-142` into a chat, and the alternative is selecting six characters of 12.5px text
 * beside a chevron. The button copies the key alone — never the title, never a URL — because that
 * is what a key is for.
 *
 * ⚠️ **No `Home` icon and no truncation-with-ellipsis of the middle.** The trail is three or four
 * segments deep here; the collapsing behaviour a deeper one would need is complexity for a case
 * this product does not have.
 */

export interface Crumb {
  /** Already translated, or a name off a record. */
  readonly label: string;
  /**
   * Where it goes. `undefined` makes it plain text — which every LAST segment is, and which an
   * intermediate one becomes when the reader may not open it.
   */
  readonly onClick?: () => void;
}

export function Breadcrumb({
  crumbs,
  copyValue,
  trailing,
}: {
  readonly crumbs: readonly Crumb[];
  /**
   * The key, shown as the last segment with a copy button beside it. Absent on a trail whose leaf
   * has no identifier worth quoting — a project's name is not a key.
   */
  readonly copyValue?: string;
  /** Anything that belongs after the trail: a status pill, a client, a mode control. */
  readonly trailing?: ReactNode;
}) {
  const t = useT();
  const { copied, copy, toaster } = useCopy();

  return (
    <>
      <nav aria-label={t('breadcrumb.label')} className="flex min-w-0 items-center gap-1">
        {crumbs.map((crumb, index) => (
          /* ⚠️ Keyed on the LABEL and its position: two segments can share a label — a project
             called *Projektek* is not impossible — and the position disambiguates without
             inventing an id the caller would have to supply. */
          <span key={`${String(index)}-${crumb.label}`} className="flex min-w-0 items-center gap-1">
            {index === 0 ? null : (
              <Icon
                name="chevron-right"
                size={13}
                className="text-muted-foreground/60 shrink-0"
                aria-hidden
              />
            )}
            {crumb.onClick === undefined ? (
              /**
               * ⚠️ **`aria-current="page"` on the leaf**, which is what tells a screen reader the
               * trail has ended rather than leaving it to the missing link to imply it.
               */
              <span
                className={
                  index === crumbs.length - 1
                    ? 'truncate text-[13.5px] font-medium'
                    : 'text-muted-foreground truncate text-[12.5px]'
                }
                aria-current={index === crumbs.length - 1 ? 'page' : undefined}
              >
                {crumb.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={crumb.onClick}
                className="text-muted-foreground hover:text-foreground truncate text-[12.5px] transition-colors"
              >
                {crumb.label}
              </button>
            )}
          </span>
        ))}

        {copyValue === undefined ? null : (
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground size-6 shrink-0"
            /* ⚠️ The label changes on success rather than a toast firing: a toast for a copy is a
               notification about something the reader just did deliberately. */
            aria-label={copied ? t('breadcrumb.copied') : t('breadcrumb.copy', { key: copyValue })}
            title={copied ? t('breadcrumb.copied') : t('breadcrumb.copy', { key: copyValue })}
            onClick={() => {
              copy(copyValue);
            }}
          >
            <Icon name={copied ? 'check' : 'copy'} size={13} />
          </Button>
        )}
      </nav>

      {trailing}
      {toaster}
    </>
  );
}

/**
 * The copy itself, shared by `Breadcrumb` and `CopyMenu`.
 *
 * ⚠️⚠️ **THE DOM TYPES PROMISE `navigator.clipboard` EXISTS AND THE RUNTIME DOES NOT.** It is
 * undefined outside a secure context — a plain-HTTP deploy, and jsdom — so a bare call throws and
 * takes the click handler with it. But an optional chain is a LINT ERROR here, because the type says
 * it cannot be null. **So it is read through `unknown` off a widened `globalThis`**, which is the
 * same escape `i18n/index.tsx` makes for `navigator.languages` and for the same reason.
 *
 * ⚠️⚠️ 3 Nov 2026 · **AND A REFUSED WRITE IS CAUGHT AND SAID.** `writeText` rejects with
 * `NotAllowedError` when the browser withholds the permission — an embedded frame, a denied prompt —
 * and the un-caught rejection surfaced as a page error with nothing on screen. The success stays on
 * the trigger (a toast for something the reader just did is noise); a failure is something they
 * did NOT do, and needs saying where they will read it.
 */
function useCopy(): { copied: boolean; copy: (value: string) => void; toaster: ReactNode } {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const copy = (value: string): void => {
    const clipboard = (globalThis as { navigator?: { clipboard?: Clipboard } }).navigator
      ?.clipboard;
    if (clipboard === undefined) return;
    void clipboard.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
        }, 1500);
      },
      () => {
        setToasts([
          {
            id: `copy-failed-${String(Date.now())}`,
            title: t('breadcrumb.copyFailed'),
            description: t('breadcrumb.copyFailedBody'),
            variant: 'error',
          },
        ]);
      },
    );
  };

  return {
    copied,
    copy,
    toaster: (
      <Toaster
        toasts={toasts}
        label={t('breadcrumb.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((toast) => toast.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    ),
  };
}

/**
 * ⚠️⚠️ **WHEN MORE THAN ONE THING IS WORTH COPYING, ASK WHICH.** Feedback round 4, item 11: a
 * project has a key, a machine identifier and an address, and a single copy button had to pick one
 * of the three — it picked the key, so the other two were reachable only by reading them off the
 * screen and retyping.
 *
 * ⚠️ **The confirmation is on the TRIGGER, not a toast**, the same choice `Breadcrumb`'s own button
 * makes: a toast announcing something the reader just deliberately did is noise, and the tick where
 * their eye already is says it faster.
 */
export function CopyMenu({
  label,
  items,
}: {
  readonly label: string;
  readonly items: readonly { label: string; value: string }[];
}) {
  const t = useT();
  /* `useCopy` above carries both arguments: the absent clipboard and the refused one. */
  const { copied, copy, toaster } = useCopy();

  return (
    <>
      <DropdownMenu
        width={260}
        align="start"
        label={label}
        items={items.map((item) => ({
          /**
           * ⚠️⚠️ **THE LABEL ONLY — no value under it.** It used to print what would land on the
           * clipboard, on the argument that seeing it first stops somebody copying the wrong one of
           * three similar strings. The product owner's answer, 20 Sep: it is helper text. The three
           * labels already say which is which, and a full URL under the third made the menu twice as
           * tall as the thing it offers.
           */
          label: item.label,
          icon: 'copy' as const,
          onSelect: () => {
            copy(item.value);
          },
        }))}
      >
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground size-6 shrink-0"
          aria-label={copied ? t('breadcrumb.copied') : label}
          title={copied ? t('breadcrumb.copied') : label}
        >
          <Icon name={copied ? 'check' : 'copy'} size={13} />
        </Button>
      </DropdownMenu>
      {toaster}
    </>
  );
}

/**
 * ⚠️⚠️ 3 Nov 2026 · **A RECORD'S ONE COPY CONTROL: ITS IDENTIFIER, OR ITS LINK.** The contract
 * page had two copies — the breadcrumb's (the identifier) and a chip in the meta line (a link built
 * without the workspace prefix, so it opened nothing). Now there is one, beside the trail, asking
 * which. `path` is workspace-relative (`/contracts/cont_12a3cd5a`); the prefix and the origin are
 * added here, so the link is the record's canonical address whether it is open as a page, a
 * drawer or a dialog over a list.
 */
export function RecordCopyMenu({
  identifier,
  path,
}: {
  readonly identifier: string;
  readonly path: string;
}) {
  const t = useT();
  const at = useWorkspacePath();
  return (
    <CopyMenu
      label={t('projects.copy.label')}
      items={[
        { label: t('projects.copy.identifier'), value: identifier },
        { label: t('projects.copy.link'), value: `${window.location.origin}${at(path)}` },
      ]}
    />
  );
}
